import { Plus, Upload as UploadIcon, FileText, PenTool, UserCheck, Lock, Globe, CloudUpload, Trash2, X, File as FileIcon, UserPlus, GripVertical, Calendar, ArrowRight, Layers, Layout, ChevronLeft, Search } from 'lucide-react';
import { Button } from '@/components/ui/button.js';
import { useCallback, useEffect, useState, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogClose, DialogFooter } from '@/components/ui/dialog.js';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Label } from '@/components/ui/label.js';
import { Input } from '@/components/ui/input';
import LoadingSpinner from '@/shared/ui/LoadingSpinner.jsx';
import { toast } from 'sonner';
import { usePostRequest, useGetRequest } from '@/shared/hooks/requests.js';
import { uploadLimits, uploadProblem, useFileDrop } from '../hooks/useFileDrop.js';
import { signerRowsProblem, useSignerList } from '../hooks/useSignerList.js';
import { ToggleButton } from './file-create/ToggleButton.jsx';
import { UploadConflictDialog } from './file-create/UploadConflictDialog.jsx';
import { FILES, ENROLL_SIGNED, ENROLL_AGREEMENT, SHARE_USERS, WORKFLOW_USERS_WITH_CERTS, FILE_VERSION_UPLOAD } from '@/shared/utils/urls.js';
import { SignerRow } from '@/modules/signing/components/SignerRow.jsx';
import { useTranslation } from 'react-i18next';
import { formatFileSize } from '@/shared/utils/helpers.js';
import { cn } from '@/lib/utils';
import { toastApiError } from '@/shared/utils/apiError.js';
import { useProjectDefaultVisibility, useProjectPolicies } from '@/shared/hooks/useProjectPolicies.js';
import { nameProblemMessage } from '@/shared/utils/filenames.js';
import { MAX_MESSAGE_LENGTH, deadlineProblem, noticePayload, toLocalInput } from '@/modules/signing/notice';

// Max files accepted in a single upload. Mirror of the backend cap in
// app/api/embed/files.py — keep the two in sync.
const MAX_UPLOAD_FILES = 10;

export const FileCreate = ({ setData, open: externalOpen, onOpenChange: externalOnOpenChange, folderId = null, initialFiles = null, hideTrigger = false }) => {
  const { t } = useTranslation();
  const projectId = localStorage.getItem('projectId');
  const [internalOpen, setInternalOpen] = useState(false);

  const open = externalOpen !== undefined ? externalOpen : internalOpen;
  const setOpen = (value) => {
    if (externalOnOpenChange) {
      externalOnOpenChange(value);
    } else {
      setInternalOpen(value);
    }
  };

  const [files, setFiles] = useState([]);
  // Null until the person picks: the project's default visibility applies.
  const projectDefaultVisibility = useProjectDefaultVisibility();
  const [chosenVisibility, setVisibility] = useState(null);
  const visibility = chosenVisibility ?? projectDefaultVisibility;
  const [approvalState, setDocumentType] = useState('ordinary');
  const [step, setStep] = useState(1); // 1: Upload/Type, 2: Signing Config
  const [signingMode, setSigningMode] = useState('sequential');
  const [message, setMessage] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const deadlineIssue = deadlineProblem(expiresAt);
  const {
    signers,
    usableSigners,
    addSigner,
    removeSigner,
    updateSigner,
    moveSigner,
    reset: resetSigners,
    draggedIndex: draggedSigner,
    setDraggedIndex: setDraggedSigner,
  } = useSignerList();
  // Agreement-specific state
  const [agreementParticipants, setAgreementParticipants] = useState([]); // [{ id, email }]
  const [participantSearch, setParticipantSearch] = useState('');
  const [uploadedFileIds, setUploadedFileIds] = useState([]);
  // The project's upload limits (console → Settings), checked before upload
  // and shown in the drop zone; the server checks them again.
  const limits = uploadLimits(useProjectPolicies());

  const acceptTypes = limits.allowedTypes.length ? limits.allowedTypes.map((ext) => `.${ext}`).join(',') : undefined;

  // Keep the files the server would take; say why each other one is left out.
  const screenFiles = useCallback((list) => Array.from(list).filter((file) => {
    const problem = uploadProblem(file, limits);
    if (!problem) return true;
    if (problem.kind === 'too_large') {
      toast.error(t('my-docs:create.upload.tooLarge', { name: file.name, max: limits.maxMb }));
    } else if (problem.kind === 'type_not_allowed') {
      toast.error(t('my-docs:create.upload.typeNotAllowed', {
        name: file.name, ext: problem.ext || '—', allowed: limits.allowedTypes.join(', '),
      }));
    } else {
      toast.error(t('my-docs:create.upload.badName', { name: file.name, problem: nameProblemMessage(t, problem.reason, 'file') }));
    }
    return false;
  }), [limits, t]);

  // When modal opens with initialFiles (from drag-and-drop), pre-populate the list
  useEffect(() => {
    if (open && initialFiles && initialFiles.length > 0) {
      if (initialFiles.length > MAX_UPLOAD_FILES) {
        toast.error(t('my-docs:create.upload.tooMany', { max: MAX_UPLOAD_FILES }));
      }
      setFiles(screenFiles(initialFiles.slice(0, MAX_UPLOAD_FILES)));
    }
    // screenFiles changes with the limits; the drop is screened once, on open
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialFiles, t]);

  const uploadDocs = usePostRequest({ url: FILES });
  const uploadVersion = usePostRequest({});
  const [conflictOpen, setConflictOpen] = useState(false);
  const [conflicts, setConflicts] = useState([]); // [{ filename, existingFileId }]
  const [resolving, setResolving] = useState(false);
  const enrollSigned = usePostRequest({});
  const enrollAgreement = usePostRequest({});
  const loadProjectUsers = useGetRequest({ url: SHARE_USERS });
  const loadProjectUsersWithCerts = useGetRequest({ url: WORKFLOW_USERS_WITH_CERTS });

  // Load project users when entering step 2 for agreement type
  useEffect(() => {
    if (step === 2 && approvalState === 'agreement') {
      loadProjectUsers.request();
    }
    if (step === 2 && approvalState === 'signed') {
      loadProjectUsersWithCerts.request();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, approvalState]);

  const projectUsersWithCerts = useMemo(() => {
    return loadProjectUsersWithCerts.response || [];
  }, [loadProjectUsersWithCerts.response]);

  const currentUserId = localStorage.getItem('userId');
  const availableProjectUsers = useMemo(() => {
    return loadProjectUsers.response || [];
  }, [loadProjectUsers.response]);

  const filteredProjectUsers = useMemo(() => {
    if (!participantSearch) return availableProjectUsers;
    const q = participantSearch.toLowerCase();
    return availableProjectUsers.filter(u =>
      (u.email || '').toLowerCase().includes(q) ||
      (u.displayName || u.display_name || '').toLowerCase().includes(q)
    );
  }, [availableProjectUsers, participantSearch]);

  const toggleParticipant = (user) => {
    setAgreementParticipants((prev) => {
      const exists = prev.some(p => p.id === user.id);
      if (exists) return prev.filter(p => p.id !== user.id);
      return [...prev, { id: user.id, email: user.email, displayName: user.displayName || user.display_name }];
    });
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    if (!selectedFiles.length) return;

    // Name, size and type, on the same rules as a drop (the project's).
    addFiles(screenFiles(selectedFiles));
    e.target.value = ''; // Reset input
  };

  const removeFile = (idx) => {
    setFiles(prev => prev.filter((_, i) => i !== idx));
  };

  // Append files while enforcing the upload cap. The native file picker can't
  // limit how many a user selects, so we reject the whole batch if it would
  // push the total over the cap — clearer than silently keeping a subset.
  const addFiles = (incoming) => {
    if (!incoming.length) return;
    setFiles((prev) => {
      if (prev.length + incoming.length > MAX_UPLOAD_FILES) {
        toast.error(t('my-docs:create.upload.tooMany', { max: MAX_UPLOAD_FILES }));
        return prev;
      }
      return [...prev, ...incoming];
    });
  };

  // The modal's own drop target, on the same rules as the whole-window one.
  const { isDragOver: isDropZoneDragOver, handlers: dropZoneHandlers } = useFileDrop({
    onFiles: (dropped) => addFiles(screenFiles(dropped)),
  });

  // Build the multipart body for a (sub)set of files. `onConflict` maps to the
  // backend's collision mode ("error" | "rename"); omitted on the first try.
  const buildUploadForm = (fileList, onConflict) => {
    const fd = new FormData();
    fd.append('project_id', projectId);
    fd.append('visibility', visibility);
    fd.append('parent_id', folderId || '');
    if (onConflict) fd.append('on_conflict', onConflict);
    fileList.forEach((file) => fd.append('uploaded_files', file));
    return fd;
  };

  // Shared post-success path: stash ids, then either advance to the signing/
  // agreement step or close out an ordinary upload.
  const finishUpload = (items) => {
    setUploadedFileIds(items.map((item) => item.id));
    if (approvalState === 'signed' || approvalState === 'agreement') {
      setStep(2);
    } else {
      toast.success(t('my-docs:create.upload.successCount', { count: files.length }));
      handleReset();
      if (setData) setData();
    }
  };

  const extractItems = (response) =>
    (Array.isArray(response) ? response : (response?.items || response?.files || []));

  const handleUpload = async (e) => {
    if (e) e.preventDefault();
    if (!files.length) {
      toast.error(t('my-docs:create.upload.fileError'));
      return;
    }
    // First attempt in the default "error" mode: the backend creates nothing
    // and returns 409 + the colliding names if any exist, so we can prompt.
    const { success, response, error } = await uploadDocs.request({
      data: buildUploadForm(files),
      showError: false,
    });
    if (success) {
      finishUpload(extractItems(response));
      return;
    }
    const conflictList = error?.status === 409 ? (error?.data?.detail?.params?.conflicts || []) : null;
    if (conflictList && conflictList.length) {
      setConflicts(conflictList);
      setConflictOpen(true);
      return;
    }
    toastApiError(error);
  };

  // "Keep both": re-upload everything letting the backend auto-suffix dupes.
  const handleConflictRename = async () => {
    setResolving(true);
    try {
      const { success, response, error } = await uploadDocs.request({
        data: buildUploadForm(files, 'rename'),
        showError: false,
      });
      if (success) {
        setConflictOpen(false);
        finishUpload(extractItems(response));
      } else {
        toastApiError(error);
      }
    } finally {
      setResolving(false);
    }
  };

  // "Replace": upload a new version of each existing file that collided, and
  // upload the remaining (non-colliding / intra-batch-dup) files normally.
  const handleConflictReplace = async () => {
    setResolving(true);
    try {
      const replaceMap = new Map(
        conflicts
          .filter((c) => (c.existingFileId ?? c.existing_file_id))
          .map((c) => [c.name, c.existingFileId ?? c.existing_file_id]),
      );
      const toReplace = [];
      const toUpload = [];
      // Each existing file is replaced by at most one local file; any extra
      // same-named files fall through to upload (auto-suffixed) instead of
      // both overwriting the same target.
      const consumedTargets = new Set();
      files.forEach((f) => {
        const targetId = replaceMap.get(f.name);
        if (targetId && !consumedTargets.has(targetId)) {
          consumedTargets.add(targetId);
          toReplace.push({ file: f, targetId });
        } else {
          toUpload.push(f);
        }
      });

      const resultItems = [];
      for (const { file, targetId } of toReplace) {
        const fd = new FormData();
        fd.append('uploaded_file', file);
        const { success, error } = await uploadVersion.request({
          url: FILE_VERSION_UPLOAD.replace('{id}', targetId),
          data: fd,
          showError: false,
        });
        if (!success) {
          toastApiError(error);
          return;
        }
        resultItems.push({ id: targetId });
      }

      if (toUpload.length) {
        // Any leftover (incl. intra-batch dups with no replace target) is
        // auto-suffixed rather than blocked.
        const { success, response, error } = await uploadDocs.request({
          data: buildUploadForm(toUpload, 'rename'),
          showError: false,
        });
        if (!success) {
          toastApiError(error);
          return;
        }
        resultItems.push(...extractItems(response));
      }

      setConflictOpen(false);
      finishUpload(resultItems);
    } finally {
      setResolving(false);
    }
  };

  const handleReset = useCallback(() => {
    setOpen(false);
    setStep(1);
    setFiles([]);
    setVisibility(null);
    setDocumentType('ordinary');
    setSigningMode('sequential');
    setMessage('');
    setExpiresAt('');
    resetSigners();
    setAgreementParticipants([]);
    setParticipantSearch('');
    setUploadedFileIds([]);
  }, []);

  const handleAgreementSubmit = async (e) => {
    if (e) e.preventDefault();

    if (agreementParticipants.length === 0) {
      toast.error(t('signing:agreement.noParticipantSelected'));
      return;
    }

    if (!uploadedFileIds || uploadedFileIds.length === 0) {
      toast.error(t('my-docs:create.upload.noFileIds'));
      return;
    }

    try {
      const results = await Promise.all(uploadedFileIds.map(fileId =>
        enrollAgreement.request({
          url: ENROLL_AGREEMENT.replace('{file_id}', fileId),
          data: {
            procedure_type: signingMode,
            participants: agreementParticipants.map((u, i) => ({
              user_id: u.id,
              participant_order: signingMode === 'sequential' ? i + 1 : 1,
            })),
          },
        })
      ));

      if (results.every(r => r.success)) {
        toast.success(t('signing:agreement.created'));
        handleReset();
        if (setData) setData();
      } else {
        toast.error(t('signing:agreement.createError'));
      }
    } catch (err) {
      console.error(err);
      toast.error(t('signing:agreement.createError'));
    }
  };

  const handleSigningSubmit = async (e) => {
    if (e) e.preventDefault();

    // A row is usable if it has either a user_id OR a non-empty PINFL.
    const usableRows = usableSigners;
    if (usableRows.length === 0) {
      toast.error(t('signing:addSigners.noSigners'));
      return;
    }
    if (deadlineIssue) {
      toast.error(t(`signing:notice.deadline_${deadlineIssue}`));
      return;
    }

    // If PINFL is typed, it must be 14 digits. Rows that have only user_id
    // (no PINFL) are accepted — the backend leaves national_id_value null.
    const rowProblem = signerRowsProblem(usableRows);
    if (rowProblem) {
      toast.error(rowProblem.problem === 'pinfl'
        ? t('signing:addSigners.invalidPinfl', { pinfl: rowProblem.value })
        : t('signing:addSigners.invalidEmail'));
      return;
    }

    const candidatesPayload = usableRows.map((s, i) => ({
      ...(s.user_id != null ? { user_id: s.user_id } : {}),
      ...(s.user_id != null && s.certificate_id != null ? { certificate_id: s.certificate_id } : {}),
      ...((s.national_id_value || '').trim() ? { national_id_value: s.national_id_value.trim() } : {}),
      full_name: (s.full_name || '').trim() || null,
      email: (s.email || '').trim() || null,
      participant_order: signingMode === 'sequential' ? i : 0,
    }));

    try {
      if (!uploadedFileIds || uploadedFileIds.length === 0) {
        console.error('No file IDs available - upload step likely failed');
        toast.error(t('my-docs:create.upload.noFileIds'));
        return;
      }

      // Atomic enrollment: type flip + collection creation in one call.
      const results = await Promise.all(uploadedFileIds.map(fileId =>
        enrollSigned.request({
          url: ENROLL_SIGNED.replace('{file_id}', fileId),
          data: {
            sign_procedure_type: signingMode,
            candidates: candidatesPayload,
            ...noticePayload({ message, expiresAt }),
          },
        })
      ));

      console.log('enrollSigned results:', results);

      if (results.every(r => r.success)) {
        toast.success(t('signing:addSigners.created'));
        handleReset();
        if (setData) setData();
      } else {
        toast.error(t('signing:addSigners.error'));
      }
    } catch (err) {
      console.error(err);
      toast.error(t('my-docs:create.upload.signingRequestError'));
    }
  };

  // Handle Enter key only if focused on something that isn't a textarea/input? 
  // Probably safe to just remove global listener to avoid accidental uploads or conflicts.
  // The form's onSubmit handles Enter.


  return (
    <>
      {!hideTrigger && (
        <Button
          onClick={() => setOpen(true)}
          variant="ghost"
          size="sm"
          className="bg-[#F2F2F2] hover:bg-[#E5E5E5] text-black dark:bg-muted dark:text-foreground dark:hover:text-primary border-none shadow-none flex items-center justify-center gap-1.5 px-4 h-9"
        >
          <UploadIcon className="w-4 h-4" />
          {t('mainPage:actions.upload')}
        </Button>
      )}
      {/* <Button
        onClick={() => setOpen(true)}
        size="sm"
        className="font-semibold text-white flex items-center justify-center gap-1.5 px-4 h-9"
        style={{
          background: 'linear-gradient(135deg, #4185F6 0%, #6FA4F9 100%)',
          border: 'none',
        }}
      >
        <Plus className="w-4 h-4" />
        {t('mainPage:actions.newDocument')}
      </Button> */}

      <Dialog open={open} onOpenChange={(val) => {
        if (!val) handleReset();
        else setOpen(true);
      }}>
        <DialogContent
          className={cn(
            "w-full max-w-[95vw] p-0 gap-0 bg-background border border-border shadow-lg overflow-hidden sm:rounded-md",
            // Step 2 signed config has wide PINFL+User+Name+Email rows.
            step === 2 && approvalState === 'signed' ? "sm:w-[1100px]" : "sm:w-[600px]",
          )}
        >
          {/* Header */}
          <div className="p-6 pb-2 relative">
            <DialogClose className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground cursor-pointer">
              <X className="h-4 w-4" />
              <span className="sr-only">Close</span>
            </DialogClose>
            <div className="flex items-start gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  {step === 1 ? <CloudUpload className="w-5 h-5 text-muted-foreground" /> : <PenTool className="w-5 h-5 text-primary" />}
                  <DialogTitle className="text-lg font-semibold text-foreground">
                    {step === 1 ? t('my-docs:create.upload.title') : t('signing:addSigners.titleSign')}
                  </DialogTitle>
                </div>
                <p className="text-sm text-muted-foreground leading-tight pr-8 break-words">
                  {step === 1 ? t('my-docs:create.upload.description') : t('signing:addSigners.description')}
                </p>
              </div>
            </div>
          </div>

          {step === 1 ? (
            <div className="p-6 pt-4 space-y-4">
              <div className="w-full">
                <form onSubmit={handleUpload}>
                  <div className="m-0">
                    {/* Upload Zone */}
                    <div
                      className="relative group h-[190px]"
                      {...dropZoneHandlers}
                    >
                      {uploadDocs.loading ? (
                        <div className="border border-dashed border-border rounded-md p-8 flex flex-col items-center justify-center bg-muted/30 h-full">
                          <LoadingSpinner className="w-6 h-6 mb-3 text-primary" />
                          <p className="text-sm font-medium text-muted-foreground">{t('my-docs:create.upload.uploading')}</p>
                        </div>
                      ) : files.length > 0 ? (
                        // File List — also accepts drops
                        <div className={cn(
                          "border rounded-md p-3 space-y-2 h-full overflow-y-auto transition-colors",
                          isDropZoneDragOver
                            ? "border-brand bg-blue-50/40 border-dashed"
                            : "border-border bg-muted/10"
                        )}>
                          {isDropZoneDragOver && (
                            <div className="flex items-center justify-center h-8 text-xs font-medium text-brand gap-1.5">
                              <UploadIcon className="w-3.5 h-3.5" />
                              Drop to add files
                            </div>
                          )}
                          {files.map((file, idx) => (
                            <div key={idx} className="flex items-center gap-3 p-3 bg-background rounded-lg border border-border shadow-sm group/file hover:border-primary/30 transition-colors w-full max-w-full overflow-hidden">
                              <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center shrink-0">
                                <FileIcon className="w-5 h-5 text-blue-600 fill-blue-100" />
                              </div>
                              <div className="flex-1 min-w-0 grid gap-0.5">
                                <TooltipProvider>
                                  <Tooltip delayDuration={300}>
                                    <TooltipTrigger asChild>
                                      <p className="text-sm font-medium text-foreground truncate pr-1 cursor-help">{file.name}</p>
                                    </TooltipTrigger>
                                    <TooltipContent className="bg-popover text-popover-foreground text-[11px] rounded-[4px] px-2 py-1.5 shadow-md border-border max-w-[300px] break-words">
                                      <p>{file.name}</p>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                                <p className="text-[10px] text-muted-foreground uppercase font-medium truncate">{file.name.split('.').pop()} &#8226; {formatFileSize(file.size)}</p>
                              </div>
                              <button
                                type="button"
                                onClick={() => removeFile(idx)}
                                className="p-1.5 hover:bg-red-50 rounded-md text-gray-400 hover:text-red-500 transition-colors cursor-pointer shrink-0"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          ))}
                          <div className="pt-1 flex justify-center">
                            <label htmlFor="file-upload-add" className="text-xs text-blue-600 hover:text-blue-700 cursor-pointer font-medium flex items-center gap-1 py-1 px-3 hover:bg-blue-50 rounded-md transition-colors">
                              <Plus className="w-3 h-3" /> Add file
                            </label>
                            <input id="file-upload-add" type="file" className="hidden" multiple accept={acceptTypes} onChange={handleFileChange} />
                          </div>
                        </div>
                      ) : (
                        // Empty Dropzone
                        <label
                          htmlFor="file-upload-initial"
                          className={cn(
                            "border border-dashed rounded-md p-6 flex flex-col items-center justify-center transition-colors cursor-pointer h-full",
                            isDropZoneDragOver
                              ? "border-brand bg-blue-50/40"
                              : "border-border bg-background hover:bg-muted/30 group-hover:border-primary/50"
                          )}
                        >
                          <div className={cn(
                            "w-10 h-10 rounded-md shadow-sm border flex items-center justify-center mb-3 transition-all",
                            isDropZoneDragOver
                              ? "bg-blue-50 border-brand/30 shadow-md"
                              : "bg-background border-border group-hover:shadow-md"
                          )}>
                            {isDropZoneDragOver
                              ? <UploadIcon className="w-5 h-5 text-brand" />
                              : <CloudUpload className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors" />
                            }
                          </div>
                          <p className={cn(
                            "text-sm font-medium mb-1 transition-colors",
                            isDropZoneDragOver ? "text-brand" : "text-foreground"
                          )}>
                            {isDropZoneDragOver ? 'Drop files here' : t('my-docs:create.upload.title')}
                          </p>
                          <p className="text-xs text-muted-foreground text-center max-w-[220px]" data-testid="upload-limits">
                            {limits.maxMb
                              ? t('my-docs:create.upload.limitHint', { max: limits.maxMb })
                              : t('my-docs:create.upload.hint')}
                            {limits.allowedTypes.length > 0 && (
                              <span className="block">{t('my-docs:create.upload.typesHint', { types: limits.allowedTypes.join(', ') })}</span>
                            )}
                          </p>
                          <input
                            id="file-upload-initial"
                            data-testid="file-upload-initial"
                            type="file"
                            className="hidden"
                            multiple
                            accept={acceptTypes}
                            onChange={handleFileChange}
                          />
                        </label>
                      )}
                    </div>
                  </div>

                  {/* Settings Container */}
                  <div className="space-y-4 pt-6 mt-4 border-t border-border">
                    {/* Visibility */}
                    <div>
                      <label className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-2 block pl-1">
                        {t('my-docs:create.upload.visibility.label')}
                      </label>
                      <div className="grid grid-cols-2 p-1 bg-muted/50 rounded-md gap-1">
                        <ToggleButton
                          active={visibility === 'private'}
                          onClick={() => setVisibility('private')}
                          icon={Lock}
                          label={t('my-docs:visibility.private')}
                        />
                        <ToggleButton
                          active={visibility === 'public'}
                          onClick={() => setVisibility('public')}
                          icon={Globe}
                          label={t('my-docs:visibility.public')}
                        />
                      </div>
                    </div>

                    {/* Doc Type */}
                    <div>
                      <label className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-2 block pl-1">
                        {t('my-docs:docType.title')}
                      </label>
                      <div className="grid grid-cols-3 p-1 bg-muted/50 rounded-md gap-1">
                        <ToggleButton
                          active={approvalState === 'ordinary'}
                          onClick={() => setDocumentType('ordinary')}
                          icon={FileText}
                          label={t('my-docs:docType.ordinary')}
                        />
                        <ToggleButton
                          active={approvalState === 'agreement'}
                          onClick={() => setDocumentType('agreement')}
                          icon={UserCheck}
                          label={t('my-docs:docType.agreement') || 'Agreement'}
                        />
                        <ToggleButton
                          active={approvalState === 'signed'}
                          onClick={() => setDocumentType('signed')}
                          icon={PenTool}
                          label={t('my-docs:docType.signed')}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Footer Buttons */}
                  <div className="flex justify-end gap-3 pt-6">
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={handleReset}
                      className="font-medium px-4 h-9"
                    >
                      {t('my-docs:btn.cancel')}
                    </Button>
                    <Button
                      type="submit"
                      disabled={
                        uploadDocs.loading ||
                        !files.length
                      }
                      className="font-semibold text-white px-6 h-9"
                    >
                      {uploadDocs.loading ? (
                        <LoadingSpinner color="#fff" className="w-4 h-4" />
                      ) : (
                        t('my-docs:create.upload.btn.submit')
                      )}
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          ) : approvalState === 'agreement' ? (
            <form onSubmit={handleAgreementSubmit} className="p-6 pt-4 space-y-6 max-h-[70vh] overflow-y-auto">
              {/* Step 2 (Agreement): pick project users */}
              <div className="space-y-6">
                {/* Procedure Mode */}
                <div className="space-y-3">
                  <Label className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider pl-1">
                    {t('signing:mode.approval')}
                  </Label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {[
                      { value: 'parallel', icon: Layers, label: 'signing:mode.parallel' },
                      { value: 'sequential', icon: ArrowRight, label: 'signing:mode.sequential' },
                    ].map((mode) => (
                      <button
                        key={mode.value}
                        type="button"
                        onClick={() => setSigningMode(mode.value)}
                        className={cn(
                          "flex items-center gap-3 p-3 rounded-lg border text-left transition-all cursor-pointer",
                          signingMode === mode.value
                            ? "border-blue-500 bg-blue-50 shadow-sm"
                            : "border-border hover:border-border/80 bg-background"
                        )}
                      >
                        <div className={cn(
                          "w-8 h-8 rounded-full flex items-center justify-center shrink-0",
                          signingMode === mode.value ? "bg-blue-500 text-white" : "bg-muted text-muted-foreground"
                        )}>
                          <mode.icon className="w-4 h-4" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-foreground">{t(mode.label)}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Selected participants chips */}
                {agreementParticipants.length > 0 && (
                  <div className="space-y-2">
                    <Label className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider pl-1">
                      Selected ({agreementParticipants.length})
                    </Label>
                    <div className="flex flex-wrap gap-2">
                      {agreementParticipants.map((p, idx) => (
                        <div
                          key={p.id}
                          className="flex items-center gap-1.5 bg-blue-50 text-blue-700 px-2 py-1 rounded-full text-xs font-medium border border-blue-200"
                        >
                          {signingMode === 'sequential' && (
                            <span className="w-4 h-4 rounded-full bg-blue-200 text-blue-800 flex items-center justify-center text-[10px] font-bold">
                              {idx + 1}
                            </span>
                          )}
                          <span className="truncate max-w-[140px]">{p.email}</span>
                          <button
                            type="button"
                            onClick={() => toggleParticipant(p)}
                            className="hover:bg-blue-200 rounded-full p-0.5"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* User picker */}
                <div className="space-y-2">
                  <Label className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider pl-1">
                    {t('signing:mode.project-users')}
                  </Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      type="text"
                      placeholder={t('signing:mode.searchPlaceholder')}
                      value={participantSearch}
                      onChange={(e) => setParticipantSearch(e.target.value)}
                      className="pl-9 h-10"
                    />
                  </div>
                  <div className="border border-border rounded-md max-h-[280px] overflow-y-auto">
                    {loadProjectUsers.loading ? (
                      <div className="flex justify-center p-4">
                        <LoadingSpinner className="w-5 h-5" color="#4185F6" />
                      </div>
                    ) : filteredProjectUsers.length === 0 ? (
                      <div className="text-center text-xs text-muted-foreground py-6">
                        {participantSearch ? 'No matching users' : 'No project users available'}
                      </div>
                    ) : (
                      <div className="divide-y divide-border">
                        {filteredProjectUsers.map((user) => {
                          const isSelected = agreementParticipants.some(p => p.id === user.id);
                          const isSelf = String(user.id) === String(currentUserId);
                          return (
                            <button
                              key={user.id}
                              type="button"
                              onClick={() => toggleParticipant(user)}
                              className={cn(
                                "w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors",
                                isSelected ? "bg-blue-50 hover:bg-blue-100" : "hover:bg-muted/50"
                              )}
                            >
                              <div className={cn(
                                "w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium shrink-0",
                                isSelected ? "bg-blue-500 text-white" : "bg-muted text-foreground"
                              )}>
                                {(user.email || 'U')[0].toUpperCase()}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium truncate">
                                  {user.email}
                                  {isSelf && (
                                    <span className="ml-2 text-[10px] font-normal text-muted-foreground">
                                      {t('signing:mode.youInitiator') || '(you — initiator)'}
                                    </span>
                                  )}
                                </p>
                                {(user.displayName || user.display_name) && (
                                  <p className="text-[11px] text-muted-foreground truncate">{user.displayName || user.display_name}</p>
                                )}
                              </div>
                              {isSelected && (
                                <div className="w-5 h-5 rounded-full bg-blue-500 text-white flex items-center justify-center shrink-0">
                                  <UserCheck className="w-3 h-3" />
                                </div>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="flex justify-between gap-3 pt-4 border-t">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setStep(1)}
                  className="font-medium px-4 h-9 flex items-center gap-2"
                >
                  <ChevronLeft className="w-4 h-4" />
                  {t('common:back')}
                </Button>
                <div className="flex gap-3">
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={handleReset}
                    className="font-medium px-4 h-9"
                  >
                    {t('my-docs:btn.cancel')}
                  </Button>
                  <Button
                    type="submit"
                    disabled={enrollAgreement.loading || agreementParticipants.length === 0}
                    className="font-semibold text-white px-6 h-9 flex items-center gap-2"
                    style={{
                      background: 'linear-gradient(135deg, #4185F6 0%, #6FA4F9 100%)',
                      border: 'none',
                    }}
                  >
                    {enrollAgreement.loading ? <LoadingSpinner color="#fff" className="w-4 h-4" /> : (
                      <>
                        {t('signing:mode.btnAggrement')}
                        <ArrowRight className="w-4 h-4" />
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </form>
          ) : (
            <form onSubmit={handleSigningSubmit} className="p-6 pt-4 space-y-6">
              {/* Step 2: Signing Config */}
              <div className="space-y-6">
                {/* Signing Mode */}
                <div className="space-y-3">
                  <Label className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider pl-1">
                    {t('signing:addSigners.signingMode')}
                  </Label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {[
                      { value: 'sequential', icon: ArrowRight, label: t('signing:mode.sequential') },
                      { value: 'parallel', icon: Layers, label: t('signing:mode.parallel') }
                    ].map((mode) => (
                      <button
                        key={mode.value}
                        type="button"
                        onClick={() => setSigningMode(mode.value)}
                        className={cn(
                          "flex items-center gap-3 p-3 rounded-lg border text-left transition-all cursor-pointer",
                          signingMode === mode.value
                            ? "border-primary bg-primary/10 shadow-sm"
                            : "border-border hover:border-border/80 bg-background"
                        )}
                      >
                        <div className={cn(
                          "w-8 h-8 rounded-full flex items-center justify-center shrink-0",
                          signingMode === mode.value ? "bg-primary text-white" : "bg-muted text-muted-foreground"
                        )}>
                          <mode.icon className="w-4 h-4" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-foreground">{mode.label}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Unified signer rows: each row is user-picker + PINFL.
                    Only this list scrolls (capped height); rows reorder by
                    dragging the grip handle (sequential mode). */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between pl-1">
                    <Label className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">
                      {t('signing:addSigners.signers')}
                    </Label>
                    <button
                      type="button"
                      onClick={addSigner}
                      className="text-xs text-primary hover:text-primary/90 font-medium flex items-center gap-1 cursor-pointer"
                    >
                      <UserPlus className="w-3.5 h-3.5" />
                      {t('signing:addSigners.addSigner')}
                    </button>
                  </div>
                  <ul className="space-y-3 max-h-[420px] overflow-y-auto pr-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                    {signers.map((row, index) => (
                      <li
                        key={index}
                        onDragOver={(e) => { if (draggedSigner !== null) e.preventDefault(); }}
                        onDrop={(e) => { e.preventDefault(); moveSigner(draggedSigner, index); setDraggedSigner(null); }}
                        className={cn(
                          'rounded-md transition-opacity',
                          draggedSigner === index && 'opacity-40',
                          draggedSigner !== null && draggedSigner !== index && 'hover:ring-2 hover:ring-primary/40'
                        )}
                      >
                        <SignerRow
                          row={row}
                          index={index}
                          signingMode={signingMode}
                          projectUsersWithCerts={projectUsersWithCerts}
                          loadingUsers={loadProjectUsersWithCerts.loading}
                          currentUserId={currentUserId}
                          canRemove={signers.length > 1}
                          onChange={(updated) => updateSigner(index, updated)}
                          onRemove={() => removeSigner(index)}
                          onDragStart={(e) => { e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', String(index)); setDraggedSigner(index); }}
                          onDragEnd={() => setDraggedSigner(null)}
                          t={t}
                        />
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Optional Message */}
                <div className="space-y-2">
                  <Label className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider pl-1">
                    {t('signing:addSigners.message')}
                  </Label>
                  <textarea
                    className="w-full bg-muted/50 border border-border rounded-md px-4 py-3 text-sm focus:ring-2 focus:ring-primary transition-all min-h-[80px] resize-none text-foreground placeholder:text-muted-foreground"
                    placeholder={t('signing:addSigners.messagePlaceholder')}
                    value={message}
                    maxLength={MAX_MESSAGE_LENGTH}
                    onChange={(e) => setMessage(e.target.value)}
                  />
                </div>

                {/* Expiration Date */}
                <div className="space-y-2">
                  <Label className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider pl-1 flex items-center gap-2">
                    <Calendar className="w-3.5 h-3.5" />
                    {t('signing:addSigners.expiresAt')}
                  </Label>
                  <input
                    type="datetime-local"
                    className="w-full bg-muted/50 border border-border rounded-md px-4 py-2.5 text-sm focus:ring-2 focus:ring-primary transition-all cursor-pointer text-foreground"
                    value={expiresAt}
                    onChange={(e) => setExpiresAt(e.target.value)}
                    min={toLocalInput(new Date())}
                    aria-invalid={deadlineIssue ? true : undefined}
                    aria-describedby={deadlineIssue ? 'upload-expires-at-error' : undefined}
                  />
                    {deadlineIssue && (
                      <p id="upload-expires-at-error" role="alert" className="text-xs text-red-600 dark:text-red-400">
                        {t(`signing:notice.deadline_${deadlineIssue}`)}
                      </p>
                    )}
                </div>
              </div>

              {/* Step 2 Footer Buttons */}
              <div className="flex justify-between gap-3 pt-4 border-t">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setStep(1)}
                  className="font-medium px-4 h-9 flex items-center gap-2"
                >
                  <ChevronLeft className="w-4 h-4" />
                  {t('signing:approvalState.back')}
                </Button>
                <div className="flex gap-3">
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={handleReset}
                    className="font-medium px-4 h-9"
                  >
                    {t('my-docs:btn.cancel')}
                  </Button>
                  <Button
                    type="submit"
                    disabled={enrollSigned.loading || Boolean(deadlineIssue)}
                    className="font-semibold text-white px-6 h-9 flex items-center gap-2"
                    style={{
                      background: 'linear-gradient(135deg, #4185F6 0%, #6FA4F9 100%)',
                      border: 'none',
                    }}
                  >
                    {enrollSigned.loading ? <LoadingSpinner color="#fff" className="w-4 h-4" /> : (
                      <>
                        {t('signing:addSigners.submit')}
                        <ArrowRight className="w-4 h-4" />
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Name-collision resolver: shown when the first upload attempt returns
          409. Lets the user replace the existing file(s) with new versions or
          keep both by auto-suffixing. */}
      <UploadConflictDialog
        open={conflictOpen}
        conflicts={conflicts}
        resolving={resolving}
        onCancel={() => setConflictOpen(false)}
        onKeepBoth={handleConflictRename}
        onReplace={handleConflictReplace}
      />
    </>
  );
};
